import { OnboardingProgress } from "@/components/OnboardingProgress";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, Sparkles, ShieldCheck, Tag, Loader2, Info } from "lucide-react";

// Isolated plan catalogue for the new checkout page. Keeps the standard /
// launch pricing self-contained so this screen never touches the live
// /billing/plans flow used by Subscribe.jsx.
const PLAN_CATALOGUE = {
  parent: {
    label: "Parent Membership",
    tagline: "The whole homeschooling community in one subscription.",
    standard_amount: 99,
    plan_id: "parent_launch",
    features: [
      "Browse & RSVP for events and meet-ups",
      "Message local vendors",
      "Access the full resource hub",
      "Join meet-up chats",
    ],
  },
  vendor: {
    label: "Vendor Membership",
    tagline: "List your business and reach local homeschooling families.",
    standard_amount: 149,
    plan_id: "vendor_launch",
    features: [
      "Listed in the vendor directory",
      "Upload logo, photos & PDF flyers",
      "Receive parent enquiries",
    ],
  },
  bundle: {
    label: "User Bundle Deal",
    tagline: "All four roles — Parent, Vendor, Event Creator & Marketplace — under one subscription.",
    standard_amount: 199,
    plan_id: "user_bundle_launch",
    features: [
      "Everything in Parent, Vendor, Event Creator & Marketplace",
      "One login, one monthly bill",
      "Priority community support",
      "Save vs. subscribing to each role separately",
    ],
  },
  eventcreator: {
    label: "Event Creator Membership",
    tagline: "Post events to the whole community calendar and sell tickets or RSVPs.",
    standard_amount: 149,
    plan_id: "event_creator_launch",
    features: [
      "Publish events on the community calendar",
      "Collect RSVPs with capacity caps",
      "Direct enquiries from parents",
      "Show your branding on each event",
    ],
  },
  marketplace: {
    label: "Marketplace Seller Membership",
    tagline: "List physical products for sale in the community marketplace.",
    standard_amount: 149,
    plan_id: "marketplace_launch",
    features: [
      "List unlimited products in the marketplace",
      "Showcase photos and descriptions",
      "Receive enquiries from parents",
      "Trusted-seller badge once approved",
    ],
  },
};

// Promotional display text, exactly per spec. Keyed by voucher code so the
// backend only has to answer "is this code valid?" — the message is a UX
// concern that lives on the page.
const PROMO_TEXT = {
  VILLAGEVIP: "3 months free, then R149/month thereafter.",
  PIONEER: "1 month free, then R99/month thereafter.",
  MEGAPASS: "R399 per month for 12 months then R599 per month",
  HIGHFIVE: "10% cashback on your first payment",
  GOLDENKEY: "Lifetime Free Access",
};

export default function Checkout() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const planKey = (params.get("plan") || "parent").toLowerCase();
  const plan = PLAN_CATALOGUE[planKey] || PLAN_CATALOGUE.parent;

  // Bundle sign-up is the one checkout flow where the user does NOT yet
  // have an account — Auth.jsx stashes credentials in sessionStorage and
  // routes here. On successful Paystack payment we hand off to
  // /bundle-onboarding, which calls /auth/bundle-complete to create all
  // four role profiles. Every other plan requires an authenticated user.
  const isAnonymousBundle = planKey === "bundle" && !user;
  const [paystackConfig, setPaystackConfig] = useState(null);
  const [paystackReady, setPaystackReady] = useState(!!window.PaystackPop);

  // Checkout requires a signed-in user — the voucher scenarios need to tag
  // the user record (free_access, has_10_percent_credit) or create a
  // Paystack subscription keyed to their email. Bounce to login first.
  // Exception: anonymous bundle sign-up (handled above) is intentionally
  // allowed through so the Bundle Deal purchase can create the account.
  useEffect(() => {
    if (user === undefined) return;
    if (user === null) return; // still loading
    if (!user) {
      if (planKey === "bundle") return; // anonymous bundle allowed
      navigate(`/login?next=/checkout?plan=${planKey}`, { replace: true });
    }
  }, [user, navigate, planKey]);

  // Load Paystack inline + public config only when we actually need them
  // (anonymous bundle flow). Mirrors the model used by the legacy
  // /bundle-subscribe page it replaces.
  useEffect(() => {
    if (!isAnonymousBundle) return;
    api.get("/billing/public-config")
      .then((r) => setPaystackConfig(r.data))
      .catch(() => setPaystackConfig({ public_key: "", gateway_ready: false }));
    if (window.PaystackPop) { setPaystackReady(true); return; }
    const existing = document.querySelector('script[src="https://js.paystack.co/v1/inline.js"]');
    if (existing) {
      existing.addEventListener("load", () => setPaystackReady(true));
      return;
    }
    const s = document.createElement("script");
    s.src = "https://js.paystack.co/v1/inline.js";
    s.async = true;
    s.onload = () => setPaystackReady(true);
    document.body.appendChild(s);
  }, [isAnonymousBundle]);

  const [voucherInput, setVoucherInput] = useState("");
  const [applying, setApplying] = useState(false);
  const [promo, setPromo] = useState(null); // { code, target_plan_id, ... }
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const promoMessage = useMemo(() => (promo ? PROMO_TEXT[promo.code] : null), [promo]);

  const applyVoucher = async () => {
    const code = voucherInput.trim().toUpperCase();
    if (!code) {
      toast.error("Please enter a voucher code.");
      return;
    }
    setApplying(true);
    try {
      const { data } = await api.get(`/promo-codes/validate/${encodeURIComponent(code)}`);
      // Reject if the voucher is plan-locked and doesn't match this checkout.
      const villageVipOk = data.code === "VILLAGEVIP" && ["vendor", "eventcreator", "marketplace"].includes(planKey);
      if (!villageVipOk && data.target_plan_id && data.target_plan_id !== plan.plan_id) {
        toast.error(`This code can’t be used on the ${plan.label}.`);
        setApplying(false);
        return;
      }
      // Unknown codes have no message — guard so we never show a blank card.
      if (!PROMO_TEXT[data.code]) {
        toast.error("That voucher code is not valid.");
        setApplying(false);
        return;
      }
      setPromo(data);
      toast.success(`Voucher ${data.code} applied!`);
    } catch (e) {
      toast.error(errMsg(e, "That voucher code is not valid."));
    } finally {
      setApplying(false);
    }
  };

  const removeVoucher = () => {
    setPromo(null);
    setVoucherInput("");
  };

  const proceed = async () => {
    if (!agree) return;

    // Anonymous bundle sign-up — Paystack inline (no account yet).
    if (isAnonymousBundle) {
      const creds = (() => {
        try { return JSON.parse(sessionStorage.getItem("cck_bundle_creds_v1") || "null"); }
        catch { return null; }
      })();
      if (!creds?.email) {
        toast.error("We lost your sign-up details — please start again.");
        navigate("/");
        return;
      }
      setSubmitting(true);
      try {
        const voucherCode = promo?.code || null;
        const { data: bp } = await api.get("/checkout/bundle-plan", {
          params: voucherCode ? { voucher_code: voucherCode } : {},
        });
        if (bp.free) {
          sessionStorage.setItem("cck_bundle_checkout_v1", JSON.stringify({ voucher_code: bp.voucher_code }));
          setSubmitting(false);
          toast.success("Lifetime free access unlocked — let's set up your 4 roles!");
          navigate("/bundle-onboarding");
          return;
        }
        if (!paystackReady || !window.PaystackPop || !paystackConfig?.gateway_ready || !paystackConfig?.public_key) {
          setSubmitting(false);
          toast.error("Payment gateway not ready — please try again in a moment.");
          return;
        }
        const handler = window.PaystackPop.setup({
          key: paystackConfig.public_key,
          email: creds.email,
          plan: bp.plan_code,
          currency: paystackConfig.currency || "ZAR",
          metadata: {
            plan_id: bp.plan_id,
            bundle: true,
            voucher_code: voucherCode,
            custom_fields: [
              { display_name: "Membership", variable_name: "membership",
                value: "User Bundle Deal — All 4 Roles" },
            ],
          },
          callback: (resp) => {
            sessionStorage.setItem("cck_bundle_checkout_v1", JSON.stringify({
              voucher_code: voucherCode, paystack_reference: resp?.reference || null,
            }));
            setSubmitting(false);
            toast.success("Payment successful — let's set up your 4 roles!");
            navigate("/bundle-onboarding");
          },
          onClose: () => {
            setSubmitting(false);
            toast.message("Checkout closed — you can resume anytime.");
          },
        });
        handler.openIframe();
      } catch (e) {
        setSubmitting(false);
        toast.error(errMsg(e, "Could not start checkout"));
      }
      return;
    }

    if (!user) {
      navigate(`/login?next=/checkout?plan=${planKey}`);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkout/initialize", {
        plan: planKey,
        voucher_code: promo?.code || null,
      });
      if (data.status === "activated") {
        // GOLDENKEY — lifetime free, no Paystack trip. The backend has
        // already flipped the user to active; refresh client state and go
        // straight to the dashboard.
        toast.success("Lifetime free access activated!");
        setTimeout(() => {
          window.location.href = data.redirect || "/dashboard";
        }, 600);
        return;
      }
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
        return;
      }
      toast.error("Could not start checkout.");
      setSubmitting(false);
    } catch (e) {
      toast.error(errMsg(e, "Could not start checkout."));
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 grain" data-testid="checkout-page">
        <OnboardingProgress />
      <div className="text-center mb-8 fade-in">
        <p className="text-sm text-stone-600 font-semibold tracking-wide uppercase">Checkout</p>
        <h1 className="text-3xl sm:text-4xl font-black text-forest mt-1" data-testid="checkout-title">
          You're subscribing to {plan.label}
        </h1>
        <p className="text-forest/60 mt-2 max-w-xl mx-auto">{plan.tagline}</p>
      </div>

      {/* Plan pricing card */}
      <div
        className="relative rounded-3xl border-2 border-sage/30 bg-white/80 shadow-sm p-7 sm:p-8 mb-6"
        data-testid="plan-pricing-card"
      >
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-terracotta-light text-terracotta-dark px-2.5 py-1 rounded-full mb-4">
          <Sparkles className="w-3 h-3" /> Selected plan
        </span>

        <h2 className="font-heading font-black text-forest text-xl">{plan.label}</h2>

        <div className="flex items-baseline gap-2 mt-3 flex-wrap" data-testid="price-row">
          {promo ? (
            <>
              <span
                className="text-2xl sm:text-3xl font-black font-heading text-forest/40 line-through"
                data-testid="standard-price-strikethrough"
              >
                R{plan.standard_amount}
              </span>
              <span className="text-xs text-forest/40">/month</span>
              <span
                className="ml-2 text-xl sm:text-2xl font-black font-heading text-sage-dark"
                data-testid="promo-price-text"
              >
                {promoMessage}
              </span>
            </>
          ) : (
            <>
              <span className="text-3xl sm:text-4xl font-black font-heading text-forest" data-testid="standard-price">
                R{plan.standard_amount}
              </span>
              <span className="text-sm text-forest/50">/month</span>
            </>
          )}
        </div>

        {promo && (
          <div
            className="mt-4 flex items-center gap-2 bg-sage-light/60 border border-sage/30 rounded-2xl px-4 py-3"
            data-testid="promo-applied-row"
          >
            <Tag className="w-4 h-4 text-sage-dark shrink-0" />
            <span className="text-sm font-bold text-forest">
              Voucher <span data-testid="applied-code">{promo.code}</span> applied
            </span>
            <button
              type="button"
              onClick={removeVoucher}
              className="ml-auto text-xs font-bold text-terracotta-dark hover:underline"
              data-testid="remove-voucher-btn"
            >
              Remove
            </button>
          </div>
        )}

        <ul className="mt-5 space-y-2">
          {plan.features.map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm text-forest/70">
              <CheckCircle2 className="w-4 h-4 text-sage-dark shrink-0 mt-0.5" /> {f}
            </li>
          ))}
        </ul>
      </div>

      {/* Voucher input */}
      <div
        className="rounded-3xl border-2 border-sage/20 bg-white/70 p-6 mb-6"
        data-testid="voucher-section"
      >
        <label className="block text-sm font-bold text-forest mb-2" htmlFor="voucher-input">
          Got a Voucher Code?
        </label>
        <div className="flex gap-2 flex-wrap sm:flex-nowrap">
          <Input
            id="voucher-input"
            value={voucherInput}
            onChange={(e) => setVoucherInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyVoucher();
              }
            }}
            disabled={applying || !!promo}
            className="rounded-full border-sage/40 bg-cream flex-1"
            data-testid="voucher-input"
          />
          <Button
            type="button"
            onClick={applyVoucher}
            disabled={applying || !voucherInput.trim() || !!promo}
            className="rounded-full bg-sage hover:bg-sage-dark text-white px-6 shrink-0"
            data-testid="apply-voucher-btn"
          >
            {applying ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Applying…
              </>
            ) : (
              "Apply"
            )}
          </Button>
        </div>
        <p className="text-[11px] text-forest/50 mt-2">
          Voucher codes are case-insensitive. One per checkout.
        </p>
      </div>

      {/* T&C + Checkout */}
      <div className="rounded-3xl border-2 border-terracotta/30 bg-terracotta-light/30 p-6" data-testid="terms-section">
        <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="terms-label">
          <input
            type="checkbox"
            checked={agree}
            onChange={(e) => setAgree(e.target.checked)}
            className="mt-0.5 w-5 h-5 shrink-0 accent-sage-dark cursor-pointer border-2 border-forest/40 rounded"
            data-testid="terms-checkbox"
          />
          <span className="text-sm text-forest leading-relaxed">
            I have read and agree to the{" "}
            <Link to="/terms" className="font-bold text-sage-dark underline" target="_blank" rel="noreferrer">
              Terms &amp; Conditions
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="font-bold text-sage-dark underline" target="_blank" rel="noreferrer">
              Privacy Policy
            </Link>
            .
          </span>
        </label>

        <div className="mt-6 text-center">
          <p
            className="text-[11px] text-forest/60 mb-3 flex items-start sm:items-center justify-center gap-1.5 max-w-md mx-auto leading-snug"
            data-testid="r1-verification-notice"
          >
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 sm:mt-0" />
            <span>Paystack charges a refundable R1 card verification fee to activate your payment method. It's refunded in full and is not part of your subscription cost.</span>
          </p>
          <Button
            size="lg"
            onClick={proceed}
            disabled={!agree || submitting}
            className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white shadow-lg px-10 hover:-translate-y-0.5 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="checkout-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Please wait…
              </>
            ) : (
              "Checkout"
            )}
          </Button>
          <p className="text-xs text-forest/50 mt-3 flex items-center justify-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5" /> You must accept the Terms &amp; Conditions to continue
          </p>
        </div>
      </div>
    </div>
  );
}
